import { z } from 'zod'
import { FlowDefinitionSchema } from './flow'
import { StepAnswersSchema } from './answers'
import { FLOW_TEMPLATES } from '../seed/foodVendor'

/**
 * สัญญาระหว่าง client กับ server — ประกาศที่เดียว ใช้ทั้งสองฝั่ง
 * ฝั่ง server validate request ด้วย schema เหล่านี้
 * ฝั่ง client parse response ด้วย schema เดียวกัน → ถ้าสัญญาเพี้ยนจะพังเสียงดังในเทส ไม่ใช่เงียบ ๆ ตอนใช้งานจริง
 */

export const ROLES = ['admin', 'field', 'report'] as const
export const RoleSchema = z.enum(ROLES)
export type Role = z.infer<typeof RoleSchema>

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'ผู้ดูแลระบบ',
  field: 'พนักงานภาคสนาม',
  /** ดูผลตอบกลับและส่งออกได้อย่างเดียว — แก้ไขอะไรไม่ได้เลย */
  report: 'ผู้ดูรายงาน',
}

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin: 'จัดการได้ทุกอย่าง ทั้งออกแบบแบบสอบถาม ผู้ใช้ และผลตอบกลับ',
  field: 'ทำแบบสอบถามที่ได้รับมอบหมายเท่านั้น',
  report: 'เห็นเฉพาะหน้าผลตอบกลับ ดูและส่งออกข้อมูลได้ แต่แก้ไข/ลบไม่ได้',
}

/** บทบาทที่เข้าหน้าผลตอบกลับได้ (ดูและแก้ไขข้อมูลที่ส่งเข้ามาแล้ว) */
export const REPORT_VIEWER_ROLES: readonly Role[] = ['admin', 'report']

// ---------------------------------------------------------------- auth

export const UserSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: RoleSchema,
  isActive: z.boolean(),
  createdAt: z.number(),
})
export type UserSummary = z.infer<typeof UserSummarySchema>

export const MeResponseSchema = z.union([
  z.object({ status: z.literal('authenticated'), user: UserSummarySchema }),
  /** ยังไม่มีผู้ใช้ในระบบเลย → ให้สร้างผู้ดูแลคนแรก */
  z.object({ status: z.literal('setupRequired') }),
  z.object({ status: z.literal('anonymous') }),
])
export type MeResponse = z.infer<typeof MeResponseSchema>

export const LoginBodySchema = z.object({
  email: z.string().email('อีเมลไม่ถูกต้อง'),
  password: z.string().min(1, 'กรุณากรอกรหัสผ่าน'),
})

export const SetupBodySchema = z.object({
  email: z.string().email('อีเมลไม่ถูกต้อง'),
  name: z.string().min(1, 'กรุณากรอกชื่อ').max(80),
  password: z.string().min(8, 'รหัสผ่านต้องยาวอย่างน้อย 8 ตัวอักษร'),
})

export const CreateUserBodySchema = z.object({
  email: z.string().email('อีเมลไม่ถูกต้อง'),
  name: z.string().min(1, 'กรุณากรอกชื่อ').max(80),
  role: RoleSchema,
  password: z.string().min(8, 'รหัสผ่านต้องยาวอย่างน้อย 8 ตัวอักษร'),
})

export const PatchUserBodySchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    role: RoleSchema.optional(),
    password: z.string().min(8, 'รหัสผ่านต้องยาวอย่างน้อย 8 ตัวอักษร').optional(),
    isActive: z.boolean().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'ไม่มีข้อมูลที่จะแก้ไข' })

export const UserListResponseSchema = z.object({ users: z.array(UserSummarySchema) })

// ---------------------------------------------------------------- workflows

export const WorkflowStatusSchema = z.enum(['active', 'archived'])

export const WorkflowSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: WorkflowStatusSchema,
  draftRev: z.number(),
  publishedVersionId: z.string().nullable(),
  publishedVersionNumber: z.number().nullable(),
  updatedAt: z.number(),
  createdAt: z.number(),
})
export type WorkflowSummary = z.infer<typeof WorkflowSummarySchema>

export const WorkflowListResponseSchema = z.object({ workflows: z.array(WorkflowSummarySchema) })

export const VersionMetaSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  versionNumber: z.number(),
  label: z.string().nullable(),
  createdAt: z.number(),
  createdByName: z.string().nullable(),
  isPublished: z.boolean(),
})
export type VersionMeta = z.infer<typeof VersionMetaSchema>

export const WorkflowDetailSchema = z.object({
  workflow: WorkflowSummarySchema,
  draft: FlowDefinitionSchema,
  assignedUserIds: z.array(z.string()),
})
export type WorkflowDetail = z.infer<typeof WorkflowDetailSchema>

export const CreateWorkflowBodySchema = z.object({
  name: z.string().min(1, 'กรุณาตั้งชื่อ').max(120),
  template: z.enum(FLOW_TEMPLATES).default('blank'),
})

export const SaveDraftBodySchema = z.object({
  definition: FlowDefinitionSchema,
  /** เลขรุ่นของร่างที่ client แก้อยู่ — ไม่ตรงกับฐานข้อมูล = 409 ไม่ทับข้อมูลคนอื่น */
  baseRev: z.number().int().min(0),
})
export const SaveDraftResponseSchema = z.object({ rev: z.number(), updatedAt: z.number() })
export const DraftConflictSchema = z.object({
  error: z.object({
    code: z.literal('conflict'),
    message: z.string(),
    details: z.object({ currentRev: z.number() }),
  }),
})

export const PatchWorkflowBodySchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    status: WorkflowStatusSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'ไม่มีข้อมูลที่จะแก้ไข' })

export const PublishBodySchema = z.object({ label: z.string().max(80).optional() })
export const PublishResponseSchema = z.object({
  versionId: z.string(),
  versionNumber: z.number(),
})

export const RestoreBodySchema = z.object({ versionId: z.string().min(1) })

export const AssignmentsBodySchema = z.object({ userIds: z.array(z.string()) })

export const VersionListResponseSchema = z.object({ versions: z.array(VersionMetaSchema) })
export const VersionDefinitionResponseSchema = z.object({
  versionId: z.string(),
  versionNumber: z.number(),
  definition: FlowDefinitionSchema,
})

// ---------------------------------------------------------------- lookup dataset (ข้อมูลอ้างอิงเก่า เช่น รายชื่อร้านปีที่แล้ว)

/**
 * ไฟล์ส่งออกจากแบบสำรวจจริงมีคอลัมน์เยอะมาก (ของจริงที่เจอ 68 คอลัมน์) — ตั้งเพดานให้เผื่อไว้มาก
 * และแถวเยอะกว่าที่ส่งไหวในคำขอเดียว จึงทยอยส่งทีละชุดแทนการตัดข้อมูลทิ้ง
 */
export const LOOKUP_MAX_COLUMNS = 300
/** จำนวนแถวต่อหนึ่งคำขอ — client แบ่งไฟล์เป็นชุด ๆ ตามขนาดนี้ */
export const LOOKUP_ROWS_PER_REQUEST = 300
/** เพดานรวมทั้งชุดข้อมูล — กันไฟล์หลุดขนาดจนฐานข้อมูลบวม */
export const LOOKUP_MAX_TOTAL_ROWS = 100_000

const LookupRowDataSchema = z.record(z.string(), z.string())

export const ImportLookupBodySchema = z.object({
  fileName: z.string().min(1).max(200),
  /** คอลัมน์ที่ใช้จับคู่ ตรงคอลัมน์ไหนก็ถือว่าเจอ (เช่น รหัสร้าน หรือ เบอร์โทรศัพท์) */
  matchColumns: z.array(z.string().min(1)).min(1, 'ต้องเลือกคอลัมน์สำหรับจับคู่อย่างน้อย 1 คอลัมน์'),
  columns: z.array(z.string().min(1)).min(1).max(LOOKUP_MAX_COLUMNS),
  rows: z
    .array(LookupRowDataSchema)
    .min(1, 'ไฟล์ไม่มีข้อมูล')
    .max(LOOKUP_ROWS_PER_REQUEST, 'ส่งข้อมูลต่อครั้งมากเกินไป'),
  /** true = ชุดแรกของไฟล์ (ล้างของเดิมทิ้งก่อน), false = ชุดถัด ๆ ไปของไฟล์เดียวกัน (ต่อท้าย) */
  replace: z.boolean(),
})

export const LookupDatasetInfoSchema = z.object({
  fileName: z.string(),
  matchColumns: z.array(z.string()),
  columns: z.array(z.string()),
  rowCount: z.number(),
  uploadedAt: z.number(),
})
/** จำนวนงานที่ไล่ตรวจ "ร้านเก่า" ย้อนหลังได้ต่อการเรียกหนึ่งครั้ง */
export const LOOKUP_RECHECK_CHUNK = 100

/**
 * ไล่ตรวจย้อนหลังว่างานที่เก็บไปแล้วตรงกับชุดข้อมูลอ้างอิงเก่าหรือไม่
 * ระบบตรวจให้เฉพาะตอนพนักงานกดบันทึกเท่านั้น งานที่เก็บก่อนนำเข้าชุดข้อมูลจึงไม่เคยถูกตรวจ
 */
export const LookupRecheckBodySchema = z.strictObject({
  /** id ของงานสุดท้ายที่ทำไปแล้ว — ส่งค่าที่ได้จากรอบก่อนกลับมาเพื่อทำต่อ */
  cursor: z.string().default(''),
})

export const LookupRecheckResponseSchema = z.object({
  processed: z.number().int(),
  /** จำนวนงานที่ "เปลี่ยนสถานะ" ในรอบนี้ (ทั้งที่เพิ่งเจอ และที่ถูกยกเลิกเพราะไม่ตรงแล้ว) */
  changed: z.number().int(),
  /** จำนวนงานที่ตรวจแล้วพบว่าเป็นร้านเก่าในรอบนี้ */
  matched: z.number().int(),
  cursor: z.string(),
  done: z.boolean(),
})

export const LookupDatasetResponseSchema = z.object({ dataset: LookupDatasetInfoSchema.nullable() })

/** ตรวจว่าค่านี้ตรงกับชุดข้อมูลอ้างอิงเก่าของ workflow นี้หรือไม่ (ใช้กับฟิลด์ที่ตั้งค่า "เช็คกับฐานข้อมูลเก่า") */
export const CheckLookupQuerySchema = z.object({
  workflowId: z.string().min(1),
  value: z.string().min(1),
})
export const CheckLookupResponseSchema = z.object({
  matched: z.boolean(),
  data: LookupRowDataSchema.nullable(),
})

// ---------------------------------------------------------------- run

export const RunnableSummarySchema = z.object({
  workflowId: z.string(),
  name: z.string(),
  versionId: z.string(),
  versionNumber: z.number(),
  publishedAt: z.number(),
})
export const RunnableListResponseSchema = z.object({ items: z.array(RunnableSummarySchema) })

// ---------------------------------------------------------------- submissions

export const SubmissionStatusSchema = z.enum(['draft', 'complete', 'incomplete'])
export type SubmissionStatus = z.infer<typeof SubmissionStatusSchema>

/** ชื่อสถานะที่แสดงให้ผู้ใช้เห็น — อยู่ที่เดียวเพื่อให้ทุกหน้าเรียกเหมือนกัน */
export const SUBMISSION_STATUS_LABELS: Record<SubmissionStatus, string> = {
  complete: 'เสร็จสมบูรณ์',
  incomplete: 'ไม่สมบูรณ์',
  draft: 'กำลังทำ',
}

export const CreateSubmissionBodySchema = z.object({
  /** id สร้างจากฝั่ง client — ส่งซ้ำกี่ครั้งก็ได้แถวเดียว */
  submissionId: z.string().uuid('รหัสงานไม่ถูกต้อง'),
  workflowId: z.string().min(1),
  versionId: z.string().min(1),
  startedAt: z.number().int(),
})

export const PutStepBodySchema = z.object({
  nodeId: z.string().min(1),
  seq: z.number().int().min(0),
  answers: StepAnswersSchema,
  answeredAt: z.number().int(),
})

export const CompleteSubmissionBodySchema = z.object({
  finalSeq: z.number().int().min(0),
  completedAt: z.number().int(),
  /** true = จบจากปุ่ม "จบการสัมภาษณ์" ก่อนถึงขั้นตอนจบจริง (ยังตอบไม่ครบ) */
  incomplete: z.boolean().optional(),
})

export const UpdateSubmissionStatusBodySchema = z.object({
  status: SubmissionStatusSchema,
})

/** แก้คำตอบของขั้นตอนหนึ่งจากหลังบ้าน (เช่นพบว่าพนักงานพิมพ์ชื่อร้านผิด) */
export const EditStepAnswersBodySchema = z.object({
  answers: StepAnswersSchema,
})

/** ตรวจว่าค่านี้ซ้ำกับคำตอบของงานอื่นในแบบสอบถามเดียวกันหรือไม่ (ใช้กับฟิลด์ที่ตั้งค่า "ห้ามข้อมูลซ้ำ") */
export const CheckUniqueQuerySchema = z.object({
  workflowId: z.string().min(1),
  fieldName: z.string().min(1),
  value: z.string().min(1),
  /** งานปัจจุบัน — ไม่นับว่าซ้ำกับตัวเอง */
  excludeSubmissionId: z.string().optional(),
})
export const CheckUniqueResponseSchema = z.object({ duplicate: z.boolean() })

export const SubmissionSummarySchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  workflowName: z.string(),
  versionId: z.string(),
  versionNumber: z.number().nullable(),
  userId: z.string(),
  userName: z.string().nullable(),
  status: SubmissionStatusSchema,
  stepCount: z.number(),
  startedAt: z.number(),
  completedAt: z.number().nullable(),
  updatedAt: z.number(),
  /** ตรงกับชุดข้อมูลอ้างอิงเก่าของ workflow นี้หรือไม่ (เช่น ร้านนี้เคยสำรวจไปแล้วปีที่แล้ว) */
  previouslySurveyed: z.boolean(),
  previousRecordData: z.record(z.string(), z.string()).nullable(),
})
export type SubmissionSummary = z.infer<typeof SubmissionSummarySchema>

export const SubmissionListQuerySchema = z.object({
  workflowId: z.string().optional(),
  status: SubmissionStatusSchema.optional(),
  q: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
})

export const SubmissionListResponseSchema = z.object({
  items: z.array(SubmissionSummarySchema),
  counts: z.object({
    total: z.number(),
    draft: z.number(),
    complete: z.number(),
    incomplete: z.number(),
  }),
  nextCursor: z.string().nullable(),
})

export const DeleteSubmissionsBodySchema = z.object({
  ids: z.array(z.string().min(1)).min(1, 'ต้องเลือกอย่างน้อย 1 รายการ').max(200),
})
export const DeleteSubmissionsResponseSchema = z.object({ deleted: z.number() })

export const SubmissionStepSchema = z.object({
  stepKey: z.string(),
  nodeId: z.string(),
  seq: z.number(),
  answers: StepAnswersSchema,
  answeredAt: z.number(),
})

export const SubmissionDetailResponseSchema = z.object({
  submission: SubmissionSummarySchema,
  steps: z.array(SubmissionStepSchema),
  definition: FlowDefinitionSchema,
})

// ---------------------------------------------------------------- uploads

export const UploadResponseSchema = z.object({
  assetId: z.string(),
  url: z.string(),
  size: z.number(),
})

export const ASSET_KINDS = ['design_image', 'logo', 'submission_photo'] as const

/** ขนาดไฟล์สูงสุดที่รับ (client บีบอัดมาแล้วควรอยู่ราว 0.5MB) */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

// ---------------------------------------------------------------- dashboard

/**
 * ★ เพดานเหล่านี้ผูกกับข้อจำกัดของ D1: หนึ่งคำสั่ง SQL รับ bound parameter ได้ราว 100 ตัว
 * query ของแดชบอร์ดเอาเงื่อนไขตัวกรองทั้งหมดไปประกอบเป็น subquery ในคำสั่งเดียว
 * จำนวนพารามิเตอร์สูงสุดจึงราว 2 + MAX_FILTERS × (3 + MAX_FILTER_VALUES) + 3 ≈ 70 — ยังปลอดภัย
 * ถ้าจะเพิ่มเพดานต้องคิดเลขนี้ใหม่ ไม่งั้นจะเจอ "too many SQL variables" ตอนผู้ใช้กรองหลายชั้น
 */
export const DASHBOARD_MAX_FILTERS = 5
/**
 * จำนวนทีมที่กรองพร้อมกันได้ — นับรวมในเพดานพารามิเตอร์ของ D1 ด้วย
 * 2 (workflow+status) + 5×13 (ตัวกรองคำถาม) + 15 (ทีม) + 3 (facet) = 85 ยังไม่เกิน 100
 */
export const DASHBOARD_MAX_TEAMS = 15
export const DASHBOARD_MAX_FILTER_VALUES = 10
/** จำนวนกราฟที่ขอได้ต่อหนึ่งครั้ง — หน้าเว็บทยอยขอเป็นชุดตามที่ผู้ใช้เลือกแสดง */
export const DASHBOARD_MAX_FACET_FIELDS = 24
/** ตัวเลือกต่อหนึ่งกราฟ — เกินนี้รวมเป็น "อื่น ๆ" ฝั่งหน้าเว็บ */
export const DASHBOARD_FACET_MAX_VALUES = 200
export const DASHBOARD_MAX_POINTS = 5000
/** จำนวนงานที่สร้างข้อมูลสรุปย้อนหลังได้ต่อการเรียกหนึ่งครั้ง */
export const DASHBOARD_REBUILD_CHUNK = 50

export const ChartableFieldSchema = z.object({
  key: z.string(),
  nodeId: z.string(),
  nodeKey: z.string(),
  fieldName: z.string(),
  label: z.string(),
  kind: z.enum(['choice', 'geo']),
  blockType: z.string(),
  options: z.array(z.string()),
  /** คีย์ของคำถามที่อยู่ในกลุ่มนี้ — มีเฉพาะรายการที่เป็นกลุ่มสรุป (รวมข้ามสาย) */
  members: z.array(z.string()).optional(),
})
export type ChartableFieldDto = z.infer<typeof ChartableFieldSchema>

/** รายชื่อแบบสอบถามแบบอ่านอย่างเดียว — ผู้ดูรายงานใช้เลือกดูข้อมูลได้โดยไม่ต้องมีสิทธิ์แก้ผัง */
export const DashboardWorkflowListSchema = z.object({
  workflows: z.array(
    z.object({ id: z.string(), name: z.string(), status: z.string() }),
  ),
})

export const DashboardMetaResponseSchema = z.object({
  workflowId: z.string(),
  workflowName: z.string(),
  total: z.number().int(),
  byStatus: z.record(z.string(), z.number().int()),
  fields: z.array(ChartableFieldSchema),
  /** พนักงาน/ทีมที่เคยเก็บงานในแบบสอบถามนี้ — ใช้ทำตัวกรอง "ทีม" */
  teams: z.array(z.object({ id: z.string(), name: z.string(), count: z.number().int() })),
  /**
   * ข้อมูลสรุปพร้อมใช้หรือยัง — งานที่บันทึกไว้ก่อนมีระบบสรุปจะยังไม่มีแถวใน submission_facts
   * หน้าเว็บจะขึ้นปุ่ม "สร้างข้อมูลสรุปย้อนหลัง" ให้เมื่อค่านี้เป็น false
   */
  factsReady: z.boolean(),
})
export type DashboardMetaResponse = z.infer<typeof DashboardMetaResponseSchema>

export const DashboardFilterSchema = z.strictObject({
  /** '<nodeId>::<fieldName>' */
  key: z.string().min(1),
  values: z.array(z.string().min(1)).min(1).max(DASHBOARD_MAX_FILTER_VALUES),
})

export const DashboardQueryBodySchema = z.strictObject({
  workflowId: z.string().min(1),
  status: SubmissionStatusSchema.nullable().default(null),
  /** กรองตามพนักงาน/ทีมที่เก็บงาน — ว่าง = ทุกทีม */
  userIds: z.array(z.string().min(1)).max(DASHBOARD_MAX_TEAMS).default([]),
  filters: z.array(DashboardFilterSchema).max(DASHBOARD_MAX_FILTERS).default([]),
  facetKeys: z.array(z.string().min(1)).max(DASHBOARD_MAX_FACET_FIELDS).default([]),
  /**
   * กลุ่มสรุป: คีย์กลุ่ม -> คีย์คำถามที่เป็นสมาชิก
   * ★ ต้องนับที่ฝั่งเซิร์ฟเวอร์ เพราะหนึ่งงานอาจตอบสมาชิกมากกว่าหนึ่งข้อ (เช่นสายเดียวกัน
   * ถามทั้งขนาด 75 G และ 210 G) ถ้าเอายอดของแต่ละข้อมาบวกกันเองจะนับงานนั้นซ้ำ
   */
  groups: z.record(z.string(), z.array(z.string().min(1)).max(12)).default({}),
  /** ใส่คีย์ของช่อง GPS เมื่อเปิดแท็บแผนที่ */
  geoKey: z.string().default(''),
})

export const DashboardFacetValueSchema = z.object({
  value: z.string(),
  count: z.number().int(),
})

export const DashboardPointSchema = z.object({
  submissionId: z.string(),
  lat: z.number(),
  lng: z.number(),
})

export const DashboardQueryResponseSchema = z.object({
  /** จำนวนงานที่ผ่านตัวกรองทั้งหมด */
  matched: z.number().int(),
  /** แยกตามสถานะ — คิดโดยไม่เอาตัวกรองสถานะมาคิด เพื่อให้เห็นสัดส่วนของทั้งชุดที่กรองไว้ */
  byStatus: z.record(z.string(), z.number().int()),
  facets: z.record(z.string(), z.array(DashboardFacetValueSchema)),
  points: z.array(DashboardPointSchema),
  pointsTruncated: z.boolean(),
})
export type DashboardQueryResponse = z.infer<typeof DashboardQueryResponseSchema>

export const DASHBOARD_ROWS_PER_PAGE = 25
/** คอลัมน์ที่ขอได้ต่อครั้ง — ผูกกับเพดานพารามิเตอร์ของ D1 เช่นเดียวกับตัวกรอง */
export const DASHBOARD_MAX_COLUMNS = 8

export const DashboardRowsBodySchema = z.strictObject({
  workflowId: z.string().min(1),
  status: SubmissionStatusSchema.nullable().default(null),
  /** กรองตามพนักงาน/ทีมที่เก็บงาน — ว่าง = ทุกทีม */
  userIds: z.array(z.string().min(1)).max(DASHBOARD_MAX_TEAMS).default([]),
  filters: z.array(DashboardFilterSchema).max(DASHBOARD_MAX_FILTERS).default([]),
  /** คอลัมน์ที่อยากเห็นในตาราง (คีย์เดียวกับกราฟ) */
  columns: z.array(z.string().min(1)).max(DASHBOARD_MAX_COLUMNS).default([]),
  page: z.number().int().min(0).default(0),
})

export const DashboardRowSchema = z.object({
  submissionId: z.string(),
  status: z.string(),
  updatedAt: z.number().int(),
  previouslySurveyed: z.boolean(),
  /** ค่าตามคอลัมน์ที่ขอ — เลือกหลายข้อจะรวมด้วยเครื่องหมายจุลภาค */
  values: z.record(z.string(), z.string()),
})

export const DashboardRowsResponseSchema = z.object({
  rows: z.array(DashboardRowSchema),
  total: z.number().int(),
  page: z.number().int(),
})
export type DashboardRowsResponse = z.infer<typeof DashboardRowsResponseSchema>

export const DashboardRebuildBodySchema = z.strictObject({
  workflowId: z.string().min(1),
  /** id ของงานสุดท้ายที่ทำไปแล้ว — ส่งค่าที่ได้จากรอบก่อนกลับมาเพื่อทำต่อ */
  cursor: z.string().default(''),
})

export const DashboardRebuildResponseSchema = z.object({
  processed: z.number().int(),
  cursor: z.string(),
  done: z.boolean(),
})

// ---------------------------------------------------------------- error

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
})
export type ApiErrorPayload = z.infer<typeof ApiErrorSchema>
